export const EPP_DISCOUNT = 5;
export const PAB_SURCHARGE = 10;

export const getInternetPabAdjustmentAmount = (plan) => {
    const amount = Number(plan?.pabAdjustmentAmount);
    return Number.isFinite(amount) && amount >= 0 ? amount : PAB_SURCHARGE;
};

export const internetMobilityDiscountEligible = (plan) => plan?.mobilityDiscountEligible !== false;
export const internetEppEligible = (plan) => plan?.eppEligible !== false;

const formatMoney = (amount) => (
    Number.isInteger(amount) ? `$${amount}` : `$${amount.toFixed(2)}`
);

const formatReadableList = (items) => {
    if (items.length <= 1) {
        return items[0] || '';
    }

    if (items.length === 2) {
        return `${items[0]} and ${items[1]}`;
    }

    return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
};

export function getInternetOfferDetail(plan, pabIncluded, mobilityDiscountActive, eppEnabled) {
    const includedDiscounts = [
        pabIncluded ? 'PAB' : null,
        mobilityDiscountActive && internetMobilityDiscountEligible(plan) ? 'TELUS/Koodo mobility' : null,
        eppEnabled && internetEppEligible(plan) ? 'EPP' : null
    ].filter(Boolean);

    if (includedDiscounts.length === 0) {
        return 'PAB not included';
    }

    const discountText = `Includes ${formatReadableList(includedDiscounts)} discount${includedDiscounts.length === 1 ? '' : 's'}`;
    return pabIncluded ? discountText : `${discountText}; PAB not included`;
}

export function getInternetPriceBreakdown(plan, pricing, pabIncluded, mobilityDiscountActive, eppEnabled) {
    if (!plan || !pricing) {
        return null;
    }

    const pabAdjustmentAmount = getInternetPabAdjustmentAmount(plan);
    const rows = [{
        label: pabIncluded ? 'Internet price with PAB' : 'Internet price without PAB',
        amount: plan.belowLine + (pabIncluded ? 0 : pabAdjustmentAmount)
    }];

    if (mobilityDiscountActive && internetMobilityDiscountEligible(plan)) {
        const mobilityAmount = Math.max(plan.belowLine - plan.mobilityBelowLine, 0);
        if (mobilityAmount > 0) {
            rows.push({ label: 'TELUS/Koodo mobility discount', amount: -mobilityAmount });
        }
    }

    if (eppEnabled && internetEppEligible(plan)) {
        const useMobilityBase = mobilityDiscountActive && internetMobilityDiscountEligible(plan);
        const eppBase = useMobilityBase ? plan.mobilityBelowLine : plan.belowLine;
        const eppPrice = useMobilityBase ? plan.eppMobilityBelowLine : Math.max(plan.belowLine - EPP_DISCOUNT, 0);
        const eppAmount = Math.max(eppBase - eppPrice, 0);
        if (eppAmount > 0) {
            rows.push({ label: 'EPP discount', amount: -eppAmount });
        }
    }

    return { rows, finalPrice: pricing.belowLine };
}

export function applyPabState(pricing, pabIncluded) {
    if (pabIncluded) {
        return pricing;
    }

    const pabAdjustmentAmount = Number.isFinite(Number(pricing.pabAdjustmentAmount))
        ? Number(pricing.pabAdjustmentAmount)
        : PAB_SURCHARGE;

    return {
        ...pricing,
        belowLine: pricing.belowLine + pabAdjustmentAmount,
        aboveLine: pricing.aboveLine === null ? null : pricing.aboveLine + pabAdjustmentAmount,
        pabDetail: `PAB not included: +${formatMoney(pabAdjustmentAmount)}`
    };
}

export function getInternetPricing(plan, mobilityDiscountActive, eppEnabled, pabIncluded) {
    if (!plan) {
        return null;
    }

    const pabAdjustmentAmount = getInternetPabAdjustmentAmount(plan);
    const mobilityEligible = internetMobilityDiscountEligible(plan);
    const eppEligible = internetEppEligible(plan);

    if (mobilityDiscountActive && mobilityEligible && eppEnabled && eppEligible) {
        return applyPabState({
            belowLine: plan.eppMobilityBelowLine,
            aboveLine: plan.eppMobilityAboveLine,
            mode: 'EPP closing tool with mobility discount',
            pabDetail: 'PAB included',
            pabAdjustmentAmount
        }, pabIncluded);
    }

    if (mobilityDiscountActive && mobilityEligible) {
        return applyPabState({
            belowLine: plan.mobilityBelowLine,
            aboveLine: plan.mobilityAboveLine,
            mode: 'Mobility discount',
            pabDetail: 'PAB included',
            pabAdjustmentAmount
        }, pabIncluded);
    }

    if (eppEnabled && eppEligible) {
        return applyPabState({
            belowLine: Math.max(plan.belowLine - EPP_DISCOUNT, 0),
            aboveLine: plan.aboveLine === null ? null : Math.max(plan.aboveLine - EPP_DISCOUNT, 0),
            mode: 'EPP closing tool',
            pabDetail: 'PAB included',
            pabAdjustmentAmount
        }, pabIncluded);
    }

    return applyPabState({
        belowLine: plan.belowLine,
        aboveLine: plan.aboveLine,
        mode: 'Below The Line',
        pabDetail: 'PAB included',
        pabAdjustmentAmount
    }, pabIncluded);
}

export function getInternetDiscounts(plan, mobilityDiscountActive, eppEnabled) {
    const discounts = [];
    if (!plan) return discounts;

    if (mobilityDiscountActive && internetMobilityDiscountEligible(plan)) {
        const mobilityAmount = Math.max(plan.belowLine - plan.mobilityBelowLine, 0);
        if (mobilityAmount > 0) {
            discounts.push({ label: 'Mobility discount - Internet', amount: mobilityAmount });
        }
    }

    if (eppEnabled && internetEppEligible(plan)) {
        const useMobilityBase = mobilityDiscountActive && internetMobilityDiscountEligible(plan);
        const eppBase = useMobilityBase ? plan.mobilityBelowLine : plan.belowLine;
        const eppPrice = useMobilityBase ? plan.eppMobilityBelowLine : Math.max(plan.belowLine - EPP_DISCOUNT, 0);
        const eppAmount = Math.max(eppBase - eppPrice, 0);
        if (eppAmount > 0) {
            discounts.push({ label: 'EPP closing tool - Internet', amount: eppAmount });
        }
    }

    return discounts;
}
